import React, { useState } from "react";
import { useReport } from "./utils/useReport";
import { Box, Divider, Grid, Modal, Typography } from "@mui/material";
import { columnsReport } from "./utils/report.columns";
import FilterReport from "./components/filterReport";
import { useNavigate } from "react-router-dom";
import { GridRowParams } from "@mui/x-data-grid";
import pathRoutes from "../../routes/pathRoutes";
import { CreatePageList } from "@app/components/Layouts/PageList";
import { Add } from "@app/components/Actions";
import {
  CustomDataGrid,
  MButton,
  MDatePicker,
  ResponsiveFormContainer,
} from "@app/components";
import AnexosCarta from "./components/subForms/anexoCarta";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { FormProvider, useForm } from "react-hook-form";
import {
  IStateReport,
  PayloadAnexos,
  ReportType,
} from "@app/types/report.types";
import { adapterAnexos } from "./utils/helpers";
import { errorAlert, successAlert } from "../../utils/alerts";
import { PermissionComponentGate } from "@app/utils/PermissionComponentGate";
import { RolesTypes } from "../users/utils/helpers";
import usePermission from "@app/context/PermissionContext";

const styleModal = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "70%",
  maxHeight: "80%",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  px: 4,
  py: 2,
  overflowY: "auto",
};

interface IFormNewGenerete {
  fecha_entrega: Date;
}

const ReportList = () => {
  const form = useForm<IFormNewGenerete>({
    mode: "all",
    defaultValues: {
      fecha_entrega: new Date(),
    },
  });

  const { isAdmin, isSuperAdmin } = usePermission();

  const {
    filteredReport,
    saveAnexos,
    updateDateDelivery,
    sendEmailReport,
    loadingSendEmail,
  } = useReport();
  const dataSamples = filteredReport();
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState<
    IStateReport & {
      type?: string;
      refReport?: string;
      resultsConsecutive?: string;
    }
  >({
    action: false,
    id: undefined,
    type: undefined,
    refReport: undefined,
    resultsConsecutive: undefined,
  });

  const handleRowDoubleClick = (params: GridRowParams): void => {
    if (isAdmin || isSuperAdmin)
      navigate(`${pathRoutes.ADMIN_INFORMES}/editar/${params.id}`);
    else navigate(`${pathRoutes.ADMIN_INFORMES}/ver/${params.id}`);
  };

  const handleCloseModal = () => {
    setOpenModal({ action: false, id: undefined, type: undefined });
  };

  const submit = async (data: Partial<ReportType> & IFormNewGenerete) => {
    if (openModal.type === "generate") {
      const payload = {
        id: openModal.id,
        fecha_entrega: form.getValues("fecha_entrega"),
        refReport: openModal.refReport,
        resultsConsecutive: openModal.resultsConsecutive,
      };
      updateDateDelivery(payload);
      handleCloseModal();
      return;
    }
    if (openModal.type === "edit") {
      navigate(`${pathRoutes.ADMIN_INFORMES}/copiar/${openModal.id}`);
      handleCloseModal();
      return;
    }

    if (openModal.type === "sendMail") {
      await sendEmailReport(openModal.id);
      handleCloseModal();
      return;
    }

    const sample = dataSamples.find((item) => item.id === openModal.id);
    if (!sample) return;

    const payload: PayloadAnexos = adapterAnexos(data, sample);

    if (
      data.anexos &&
      data.anexos.some((anexo) => anexo === "anexo_2") &&
      data.operation_limit_date?.includes("No tiene fecha de Lim.") &&
      data.operation_start_date?.includes("No tiene fecha de In.")
    ) {
      errorAlert(
        "Recuerda la instalación elegida no posee fechas límite para el Anexo II"
      );
      return;
    }

    const status = await saveAnexos({ ...payload, id: openModal.id });
    if (status) {
      handleCloseModal();
    }
  };

  const handleAnexar = (params: GridRowParams): void => {
    setOpenModal({ action: true, id: Number(params.id), type: "anexar" });
  };

  const handleGenerate = (params: GridRowParams): void => {
    setOpenModal({
      action: true,
      id: Number(params.id),
      type: "generate",
      refReport: `${(params as any).ref_report}`,
      resultsConsecutive: `${(params as any).results_consecutive}`,
    });
  };

  const handleEditar = (params: GridRowParams): void => {
    setOpenModal({
      action: true,
      id: Number(params.id),
      type: "edit",
      refReport: `${(params as any).ref_report}`,
      resultsConsecutive: `${(params as any).results_consecutive}`,
    });
  };

  const handleSendMail = (params: GridRowParams): void => {
    setOpenModal({
      action: true,
      id: Number(params.id),
      type: "sendMail",
      refReport: `${(params as any).ref_report}`,
      resultsConsecutive: `${(params as any).results_consecutive}`,
    });
  };

  return (
    <CreatePageList
      title="Listado de informes"
      buttonRight={
        <PermissionComponentGate
          permissions={[RolesTypes.ADMIN, RolesTypes.SUPER_ADMIN]}
        >
          <Add to={`admin/informes/nuevo`} title="Nuevo informe" />
        </PermissionComponentGate>
      }
    >
      <FilterReport />
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", mt: 1 }}>
        <CustomDataGrid
          rows={dataSamples}
          columns={
            columnsReport(
              handleAnexar,
              handleGenerate,
              handleEditar,
              handleSendMail
            ) as any
          }
          search={false}
          handleRowDoubleClick={handleRowDoubleClick}
        />
      </Box>

      <Modal
        open={openModal.action}
        onClose={handleCloseModal}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={{ ...styleModal }}>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(submit)}>
              <Grid item xs={12} my={2}>
                <Typography variant="tagText">
                  {openModal.type === "anexar"
                    ? "Seleccione los anexos"
                    : openModal.type === "generate"
                    ? "Va a generar el informe ¿ha sido enviado al cliente?"
                    : openModal.type === "sendMail"
                    ? "¿Está seguro de que desea enviar el informe al cliente?"
                    : "Editar informe"}
                </Typography>
                <Divider sx={{ mt: 1 }} />
              </Grid>

              {openModal.type === "anexar" ? (
                <AnexosCarta
                  mode={MODES_OPTIONS.MODAL}
                  idReport={Number(openModal.id)}
                />
              ) : openModal.type === "generate" ? (
                <ResponsiveFormContainer columns={3}>
                  <MDatePicker
                    name="fecha_entrega"
                    pickerType="date"
                    label="Fecha entrega"
                    validation={{
                      required: "La fecha de entrega es requerida",
                    }}
                  />
                </ResponsiveFormContainer>
              ) : openModal.type === "sendMail" ? (
                <Typography variant="generalText">
                  {`¿Desea enviar el informe ${openModal?.refReport || ""}/${
                    openModal?.resultsConsecutive || ""
                  } al cliente?`}
                </Typography>
              ) : (
                <Typography variant="generalText">
                  {`El informe ${openModal?.refReport}/${openModal?.resultsConsecutive}  ya ha sido enviado. Se generará un nuevo
              informe a partir del anterior.`}
                </Typography>
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 2,
                  gap: 2,
                }}
              >
                <MButton
                  text={openModal.type === "edit" ? "Continuar" : "Guardar"}
                  type="submit"
                  variant="contained"
                  loading={loadingSendEmail}
                />
                <MButton
                  onClick={handleCloseModal}
                  text="Cerrar"
                  variant="outlined"
                  loading={loadingSendEmail}
                />
              </Box>
            </form>
          </FormProvider>
        </Box>
      </Modal>
    </CreatePageList>
  );
};

export default ReportList;
